import { Navigate, RouteObject } from 'react-router-dom'
import SchoolDashboard from '../../../pages/Dashboard/schools/SchoolDashboardLayout'
import { lazy } from 'react'
import { tabNames } from '../../helpers'
import BookingsPageLayout from '../../../pages/Dashboard/schools/BookingsPage/layout'
import MySchoolLayout from '../../../pages/Dashboard/MySchoolPage'
import dayjs from '../../../helpers/dayjs'
import { redirectToDefaultSchoolLoader } from '../../../middleware'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import { BookingsRoute, HomeRoute, MySchoolRoute } from './helpers'
import { SCHOOL_LOADING_ERROR_CODES } from '../../../components/LoadingSpinner/loadingErrorCodes'
import { SchoolLayout } from '../../../pages/Dashboard/schools/SchoolLayout'
import { schoolMembershipMiddleware } from '../../../middleware/school'

const HomePage = lazy(() =>
    import('../../../pages/Dashboard/HomePage/SchoolHomePage').then((m) => ({
        default: m.default,
    }))
)

// My School pages
const Overview = lazy(() =>
    import('../../../pages/Dashboard/MySchoolPage/overview').then((m) => ({
        default: m.default,
    }))
)
const Invoices = lazy(() =>
    import('../../../pages/Dashboard/MySchoolPage/invoices').then((m) => ({
        default: m.default,
    }))
)
const Policies = lazy(() =>
    import('../../../pages/Dashboard/MySchoolPage/policies').then((m) => ({
        default: m.default,
    }))
)
const Team = lazy(() =>
    import('../../../pages/Dashboard/MySchoolPage/team').then((m) => ({
        default: m.default,
    }))
)

// Bookings Pages
const BookingsPage = lazy(() =>
    import('../../../pages/Dashboard/schools/BookingsPage').then((m) => ({
        default: m.default,
    }))
)
const BookingDatePage = lazy(() =>
    import('../../../pages/Dashboard/schools/BookingsPage/BookingDate').then(
        (m) => ({
            default: m.default,
        })
    )
)

// Misx
const SettingsPage = lazy(() =>
    import('../../../pages/Dashboard/SettingsPage/SchoolSettingsPage').then(
        (m) => ({
            default: m.default,
        })
    )
)

const PageNotFound = lazy(() =>
    import('../../../components/ErrorFallback/PageNotFound').then((m) => ({
        default: m.default,
    }))
)
const schoolRoutes: RouteObject = {
    path: 'school',
    element: <SchoolDashboard />,
    children: [
        {
            index: true,
            loader: redirectToDefaultSchoolLoader,
            element: (
                <LoadingSpinner
                    errorCode={SCHOOL_LOADING_ERROR_CODES.DEFAULT_SCHOOL.code}
                />
            ),
        },
        {
            path: ':schoolId',
            middleware: [schoolMembershipMiddleware],
            element: <SchoolLayout />,
            children: [
                {
                    index: true,
                    element: (
                        <HomeRoute>
                            <HomePage />
                        </HomeRoute>
                    ),
                    handle: {
                        meta: {
                            title: 'Home - Spark Relief Teachers',
                            description: 'Manage your account here',
                        },
                    },
                },
                {
                    path: tabNames.app.school.mySchool.index,
                    element: (
                        <MySchoolRoute>
                            <MySchoolLayout />
                        </MySchoolRoute>
                    ),
                    children: [
                        {
                            index: true,
                            element: <Navigate to={'overview'} replace />,
                        },
                        {
                            path: tabNames.app.school.mySchool.overview,
                            element: <Overview />,
                        },
                        {
                            path: tabNames.app.school.mySchool.invoices,
                            element: <Invoices />,
                        },
                        {
                            path: tabNames.app.school.mySchool.policies,
                            element: <Policies />,
                        },
                        {
                            path: tabNames.app.school.mySchool.team,
                            element: <Team />,
                        },
                    ],
                    handle: {
                        meta: {
                            title: 'My School - Spark Relief Teachers',
                            description: 'Manage your school account here',
                        },
                    },
                },
                {
                    path: tabNames.app.school.bookings.index,
                    element: (
                        <BookingsRoute>
                            <BookingsPageLayout />
                        </BookingsRoute>
                    ),
                    children: [
                        {
                            index: true,
                            element: <Navigate to={'upcoming'} replace />,
                        },
                        {
                            path: tabNames.app.school.bookings.upcoming,
                            element: <BookingsPage />,
                            handle: {
                                meta: {
                                    title: 'Upcoming Bookings - Spark Relief Teachers',
                                    description:
                                        'Manage your upcoming bookings here',
                                },
                            },
                        },
                        {
                            path: tabNames.app.school.bookings.all,
                            element: <BookingsPage />,
                            handle: {
                                meta: {
                                    title: 'All Bookings - Spark Relief Teachers',
                                    description: 'Manage all bookings here',
                                },
                            },
                        },
                        {
                            path: tabNames.app.school.bookings.date.index,
                            children: [
                                {
                                    index: true,
                                    element: (
                                        <Navigate
                                            to={`${tabNames.app.school.index}`}
                                        />
                                    ),
                                },
                                {
                                    path: ':date',
                                    children: [
                                        {
                                            index: true,
                                            element: (
                                                <Navigate
                                                    to={
                                                        tabNames.app.school
                                                            .bookings.date
                                                            .overview
                                                    }
                                                />
                                            ),
                                        },
                                        {
                                            path: tabNames.app.school.bookings
                                                .date.overview,
                                            element: <BookingDatePage />,
                                        },
                                        {
                                            path: tabNames.app.school.bookings
                                                .date.bookable,
                                            element: <BookingDatePage />,
                                        },
                                    ],
                                    handle: {
                                        meta: ({ params }: { params: any }) => {
                                            const formattedDate = dayjs(
                                                params.date,
                                                'DD-MM-YYYY'
                                            ).format('MMM Do')
                                            return {
                                                title: `${formattedDate || 'Booking'} - Spark Relief Teachers`,
                                                description: `Manage bookings for ${formattedDate}`,
                                            }
                                        },
                                    },
                                },
                            ],
                        },
                    ],
                },
                {
                    path: tabNames.app.school.settings.index,
                    element: <SettingsPage />,
                    handle: {
                        meta: {
                            title: 'Settings - Spark Relief Teachers',
                            description: 'Update your basic information',
                        },
                    },
                },
                {
                    path: '*',
                    element: (
                        <PageNotFound homeLink={tabNames.app.school.index} />
                    ),
                    handle: {
                        meta: {
                            title: 'Page not found - Spark Relief Teachers',
                        },
                    },
                },
            ],
        },
        {
            path: '*',
            element: <PageNotFound homeLink={tabNames.app.school.index} />,
            handle: {
                meta: {
                    title: 'Page not found - Spark Relief Teachers',
                },
            },
        },
    ],
}

export default schoolRoutes
